import { useEffect, useState } from 'react'

import { RiCloseLine, RiFileCloudLine, RiLoader5Line, RiUploadCloudLine } from '@remixicon/react'
import { useDropzone } from 'react-dropzone'

import { cn } from '../utils'
import { Button } from './Button'

interface IDropzoneProps extends React.HTMLAttributes<HTMLDivElement> {
  acceptsOnlyImages?: boolean
  acceptsOnlyPdf?: boolean
  description?: string
  disabled?: boolean
  loading?: boolean
  multiple?: boolean
  onDropAccepted: (files: File[]) => void
  onRemoveFile?: (fileIndex: number) => void
  subDescription?: string
}

export const Dropzone = ({
  acceptsOnlyImages = false,
  acceptsOnlyPdf = false,
  className,
  description,
  disabled = false,
  loading,
  multiple = false,
  onDropAccepted,
  onRemoveFile,
  subDescription,
}: IDropzoneProps) => {
  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([])
  const [isInputMounted, setIsInputMounted] = useState<boolean>(true)
  const accepts: Record<string, string[]> =
    !acceptsOnlyPdf && !acceptsOnlyImages
      ? {
          'application/msword': [], // .doc
          'application/pdf': [],
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [], // .docx
        }
      : acceptsOnlyImages
        ? { 'image/jpeg': [], 'image/jpg': [], 'image/png': [] }
        : { 'application/pdf': [] }
  const { getInputProps, getRootProps, isDragAccept, isDragReject, isFocused, open } = useDropzone({
    accept: accepts,
    disabled: loading || disabled,
    multiple,
    noClick: true,
    noKeyboard: true,
    onDropAccepted: (files: File[]) => {
      setAcceptedFiles(files)
      onDropAccepted(files)
    },
  })

  const rootClassName = cn(
    'flex min-h-[225px] items-center justify-center rounded-xl border border-dashed border-border-secondary p-8',
    isFocused && 'border-blue-500 bg-background-background/70',
    isDragAccept && 'border-green-500',
    isDragReject && 'border-destructive'
  )

  const showDropzone = acceptedFiles.length === 0 || multiple

  const handleRemoveFile = (fileIndex: number) => {
    setAcceptedFiles((prevFiles) => prevFiles.filter((_, index) => index !== fileIndex))
    onRemoveFile?.(fileIndex)
    setIsInputMounted(false)
  }

  useEffect(() => {
    if (!isInputMounted) {
      setIsInputMounted(true)
    }
  }, [isInputMounted])

  return (
    <div className={cn('flex flex-col gap-4 ', className)}>
      {showDropzone && (
        <div {...getRootProps({ className: rootClassName })}>
          {isInputMounted && <input {...getInputProps()} />}
          <div className="flex flex-col items-center justify-center gap-4 text-center">
            <RiUploadCloudLine className="text-green-500" size={48} />
            <p className="font-semibold text-stone-400">
              {description ?? 'Drag and drop your file here'} or{' '}
              <button
                className="cursor-pointer text-blue-500 underline"
                onClick={open}
                type="button"
              >
                browse
              </button>
            </p>
            {!acceptsOnlyPdf && !acceptsOnlyImages ? (
              <p className="text-sm text-stone-500">Accepted File Types: .pdf, .doc, and .docx</p>
            ) : acceptsOnlyImages ? (
              <p className="text-sm text-stone-500">Accepted File Types: .png, .jpeg, and .jpg</p>
            ) : (
              <p className="text-sm text-stone-500">Accepted File Types: .pdf</p>
            )}
            {subDescription && <p className="text-sm text-stone-500">{subDescription}</p>}
          </div>
        </div>
      )}

      {acceptedFiles.length > 0 && (
        <ul className="flex flex-row gap-2 flex-wrap overflow-y-auto max-h-[400px]">
          {acceptedFiles.map((file, index) => {
            return (
              <UploadedFile
                file={file}
                fileIndex={index}
                isLoading={loading}
                key={file.name}
                onRemoveFile={handleRemoveFile}
              />
            )
          })}
        </ul>
      )}
    </div>
  )
}

const UploadedFile = ({
  file,
  fileIndex,
  isLoading,
  onRemoveFile,
}: {
  file: File
  fileIndex: number
  isLoading
  onRemoveFile: (fileIndex: number) => void
}) => {
  return (
    <li
      className="border-border-secondary bg-background-secondary flex h-20 w-full flex-row items-center
        justify-between rounded-xl border px-4 py-2"
    >
      <div className="flex flex-row items-center gap-4">
        {isLoading ? (
          <RiLoader5Line className="text-green-500 animate-spin" size={32} />
        ) : (
          <RiFileCloudLine className="text-green-500" size={32} />
        )}
        <div className="overflow-ellipsis font-mono text-sm">
          <p className="font-semibold">{file.name}</p>
          <p className="text-muted-foreground">{Math.floor(file.size / 1024)}KB</p>
        </div>
      </div>
      <Button onClick={() => onRemoveFile(fileIndex)} title="Remove file" variant="ghost">
        <RiCloseLine className="cursor-pointer text-stone-400 hover:text-white" size={14} />
      </Button>
    </li>
  )
}
